import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { Box, Stack, Theme, useMediaQuery } from "@mui/material";

import AtomAssistLogo from "src/views/assistant/components/AssistantLogo";
import AAAssistantText from "src/views/assistant/components/AAAssistantText";
import AtomAssistTypingIndicator from "src/reusable/Chat/ChatMessage/AtomAssistTypingIndicator";
import ChatContainer from "src/reusable/Chat/ChatContainer";
import { ChatWrapperContainer } from "src/reusable/Chat/StyledContainers/StyledContainers";
import ResizablePanel from "src/reusable/ResizablePanel";
import useLoadDialogue from "src/views/assistant/hooks/useLoadDialogue";
import useStartChatOnMount from "src/reusable/Chat/hooks/useStartChatOnMount";
import { buildMessageTree } from "src/reusable/Chat/ChatStreaming/services/chatHelpers";
import {
  useActiveDatasetIds,
  useChatStore,
  useSourceDataRecordIds,
} from "src/views/assistant/stores/ChatStore";

import VnvAssistHeader from "./components/VnvAssistHeader";
import VnvAssistChatInput from "./components/VnvAssistChatInput";
import VnvAssistDocumentPanel from "./components/VnvAssistDocumentPanel";
import { ChatStreamProvider } from "src/reusable/Chat/ChatStreaming/context/ChatStreamContext";
import { VnvAssistSidebar } from "./components/VnvAssistSidebar";
import { useVnvAssistChat } from "./hooks/useVnvAssistChat";
import { useVnvAssistIdentification } from "./hooks/useVnvAssistIdentification";
import { useVnvAssistStore } from "./store/VnvAssistStore";
import { useGetVnvAssistDialogueData } from "./services/VnvAssistServices";
import { getMultipleDatasets } from "src/views/datasets/services/DatasetServices";
import { fetchEnrichedArtifacts } from "src/views/products/workflow/services/ArtifactEnrichmentService";

type VnvAssistChatProps = {
  vnvRunId: number;
  dialogueId: number;
  initialUserMessage?: string;
};

const VnvAssistChat = ({
  vnvRunId,
  dialogueId,
  initialUserMessage,
}: VnvAssistChatProps) => {
  const history = useHistory();
  const isLgScreen = useMediaQuery((theme: Theme) =>
    theme.breakpoints.up("xl"),
  );

  const {
    config: chatConfig,
    setActiveDialogueId,
    setDropdownDatasets,
    setDialogue: setStoreDialogue,
  } = useChatStore();

  const dropdownDatasetsLength = useChatStore(
    (s) => s.dropdownDatasets?.length ?? 0,
  );
  const activeDatasetIds = useActiveDatasetIds();
  const sourceDataRecordIds = useSourceDataRecordIds();

  const { setVnvRunId } = useVnvAssistStore();

  // Sync the store to the current route's run.
  useEffect(() => {
    setVnvRunId(vnvRunId);
    useVnvAssistStore.setState({
      dialogueId,
      claims: [],
      activeClaim: null,
      validationStatus: "idle",
      validationError: null,
      identificationTotalChunks: null,
      identificationChunksCompleted: null,
      selectedArtifact: null,
    });
  }, [vnvRunId, dialogueId, setVnvRunId]);

  useEffect(() => {
    if (dialogueId) setActiveDialogueId(dialogueId);
  }, [dialogueId, setActiveDialogueId]);

  const { data: vnvDialogueData } = useGetVnvAssistDialogueData(dialogueId);

  useEffect(() => {
    let isMounted = true;
    const loadDatasets = async () => {
      const ids = vnvDialogueData?.dataset_ids;
      if (!ids?.length) return;
      const current = useChatStore.getState().dropdownDatasets;
      const currentIds = new Set(current?.map((d) => d.id));
      if (ids.every((id) => currentIds.has(id))) return;
      try {
        const fetched = await getMultipleDatasets(ids);
        if (isMounted && fetched?.length) setDropdownDatasets(fetched);
      } catch {
        // ignore
      }
    };
    loadDatasets();
    return () => {
      isMounted = false;
    };
  }, [
    vnvDialogueData?.dataset_ids,
    dialogueId,
    dropdownDatasetsLength,
    setDropdownDatasets,
  ]);

  // Hydrate selectedArtifact from the run's input_artifact_id on reload so
  // the document preview renders without requiring a fresh upload.
  useEffect(() => {
    const artifactId = vnvDialogueData?.input_artifact_id;
    if (!artifactId) return;
    const current = useVnvAssistStore.getState().selectedArtifact;
    if (current?.id === artifactId) return;

    let isMounted = true;
    fetchEnrichedArtifacts([artifactId])
      .then((results) => {
        if (!isMounted || !results.length) return;
        useVnvAssistStore.getState().setSelectedArtifact(results[0]);
      })
      .catch(() => {
        // ignore — the panel falls back to "No document selected"
      });
    return () => {
      isMounted = false;
    };
  }, [vnvDialogueData?.input_artifact_id]);

  const { dialogue: loadedDialogue } = useLoadDialogue({ initialUserMessage });

  // Memoize dialogue tree building
  const dialogueTree = useMemo(() => {
    if (
      loadedDialogue &&
      loadedDialogue.messages &&
      loadedDialogue.messages.length > 0
    ) {
      return buildMessageTree(loadedDialogue.messages);
    }
    return [];
  }, [loadedDialogue]);

  const [docPanelWidth, setDocPanelWidth] = useState(isLgScreen ? 450 : 350);

  const datasetsToUse = useMemo(
    () => activeDatasetIds.map((id) => ({ id })),
    [activeDatasetIds],
  );

  const chatConfigMemo = useMemo(
    () => ({
      dialogueId,
      modelId: chatConfig?.modelId,
      modelVersionNum: chatConfig?.modelVersionNum,
      datasetsForSearchContext: datasetsToUse,
      sourceDataRecordIds:
        sourceDataRecordIds.length > 0 ? sourceDataRecordIds : undefined,
      numSearchResults: chatConfig?.topSearchResults || 5,
      textGenModelParams: chatConfig?.chatControlMetadata,
      systemPrompt: chatConfig?.systemPrompt,
      toolCalling: true,
      chatConfigId: chatConfig?.chatConfigId,
      product: "vnv-assist",
    }),
    [
      dialogueId,
      chatConfig?.modelId,
      chatConfig?.modelVersionNum,
      chatConfig?.chatControlMetadata,
      chatConfig?.systemPrompt,
      chatConfig?.chatConfigId,
      chatConfig?.topSearchResults,
      datasetsToUse,
      sourceDataRecordIds,
    ],
  );

  const {
    dialogue,
    responseSources,
    setResponseSources,
    terminateConnection,
    isConnected: isChatConnected,
    isConnectionError: isChatConnectionError,
    streamError: chatStreamError,
    isEnded: isChatEnded,
    isLoading: isChatLoading,
    isMessageStreamStopped: isChatMessageStreamStopped,
    emptySubmission,
    clearChatErrors,
    sendUserMessage,
    resendUserMessage,
    regenerateResponse,
    setAsPreferredMessage,
    setDialogue,
    reconstructArtifactsFromMessages,
    reset: resetChatStream,
    tokenUsage,
    setTokenUsage,
    activeSubAgents,
    store: chatStore,
  } = useVnvAssistChat(chatConfigMemo, dialogueTree);

  useEffect(() => {
    if (dialogueTree.length > 0) {
      setDialogue(dialogueTree);
      setStoreDialogue(dialogueTree);
      reconstructArtifactsFromMessages(dialogueTree);
    }
  }, [
    dialogueTree.length,
    dialogueId,
    setDialogue,
    setStoreDialogue,
    reconstructArtifactsFromMessages,
  ]);

  useEffect(() => {
    if (loadedDialogue?.config?.token_usage) {
      setTokenUsage(loadedDialogue.config.token_usage);
    }
  }, [loadedDialogue?.config?.token_usage, dialogueId, setTokenUsage]);

  useStartChatOnMount({
    initialUserMessage,
    emptySubmission,
    sendUserMessage,
  });

  const { startIdentification } = useVnvAssistIdentification();

  // Single entry point on mount: POST /identify is start-or-subscribe.
  useEffect(() => {
    if (!vnvRunId) return;
    const { validationStatus } = useVnvAssistStore.getState();
    if (validationStatus !== "idle") return;
    const controller = new AbortController();
    startIdentification(controller.signal);
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vnvRunId]);

  const onNewChat = useCallback(() => {
    useChatStore.getState().clearUploadedFiles();
    history.push("/vnv-assist");
  }, [history]);

  useEffect(() => {
    return () => {
      resetChatStream();
      terminateConnection();
      clearChatErrors();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <VnvAssistHeader
        onNewChat={onNewChat}
        dialogueId={dialogueId}
        dialogue={dialogue}
      />

      <ChatStreamProvider store={chatStore}>
        <Stack
          direction="row"
          sx={{ height: "calc(100vh - 97px)", overflow: "hidden" }}
        >
          {/* Chat pane */}
          <ChatWrapperContainer>
            <ChatContainer
              activeSubAgents={activeSubAgents}
              dialogueId={dialogueId}
              dialogue={dialogue}
              isChatConnectionError={isChatConnectionError}
              chatStreamError={chatStreamError}
              isChatConnected={isChatConnected}
              isChatEnded={isChatEnded}
              isChatLoading={isChatLoading}
              isChatMessageStreamStopped={isChatMessageStreamStopped}
              startNewChat={onNewChat}
              resendUserMessage={resendUserMessage}
              regenerateResponse={regenerateResponse}
              setAsPreferredMessage={setAsPreferredMessage}
              CustomLoadingIndicator={AtomAssistTypingIndicator}
              ChatPromptInput={
                <VnvAssistChatInput
                  chatStreamError={chatStreamError}
                  terminateConnection={terminateConnection}
                  sendUserMessage={sendUserMessage}
                  isChatConnectionError={isChatConnectionError}
                  isChatConnected={isChatConnected}
                  isChatLoading={isChatLoading}
                  isChatEnded={isChatEnded}
                  tokenUsage={tokenUsage}
                />
              }
              BackgroundComponent={
                <AtomAssistLogo lowerMessage="Ask questions about the document or review validation results." />
              }
              CustomAssistantText={AAAssistantText}
              CustomAssistantTextProps={{
                isChatConnected,
                responseSources,
                onSourcesUpdate: setResponseSources,
              }}
            />
          </ChatWrapperContainer>

          {/* Document panel */}
          <ResizablePanel
            width={docPanelWidth}
            onResize={setDocPanelWidth}
            minWidth={250}
            maxWidth={700}
            handlePosition="left"
            sx={{
              border: "1px #d8d8d8 solid",
              borderTop: "none",
              backgroundColor: "#FAFAFA",
            }}
          >
            <Box sx={{ height: "100%", overflow: "hidden" }}>
              <VnvAssistDocumentPanel />
            </Box>
          </ResizablePanel>

          {/* Sidebar */}
          <VnvAssistSidebar
            responseSources={responseSources}
            dialogueId={dialogueId}
          />
        </Stack>
      </ChatStreamProvider>
    </>
  );
};

export default VnvAssistChat;
